import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Card, Col, Badge } from "react-bootstrap";
import Breadcrumb from "Common/BreadCrumb";
import CountUp from "react-countup";
import {
  SoldeResponse,
  useFetchSmSQuery,
  useFetchsoldeMutation,
} from "features/sms/smsSlice";
import { useFetchAllSmSQuery } from "features/smsEnseignants/smsEnseignantSlice";
import { IncomeStatisticsCharts, PurchaseCharts } from "./StatisticsCharts";

const SmsStatistiques = () => {
  const { data: allSms = [] } = useFetchSmSQuery();
  const { data: allSmsEnseignant = [] } = useFetchAllSmSQuery();
  const [getSolde] = useFetchsoldeMutation();

  const [solde, setSolde] = useState<SoldeResponse>();
  const [error, setError] = useState<any>();
  const [selectedMonth, setSelectedMonth] = useState("");
  const getMonthAndYear = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    return { currentMonth, currentYear };
  };

  const { currentMonth, currentYear } = getMonthAndYear();

  const sentSmses = allSms.filter((sms) => sms.status === "sent");

  const sentEnseignantSmses = allSmsEnseignant.filter(
    (sms) => sms.status === "sent",
  );

  useEffect(() => {
    const fetchSolde = async () => {
      try {
        const result = await getSolde({
          api_key: `${process.env.REACT_APP_KEY}`,
        }).unwrap();

        setSolde(result);
      } catch (err) {
        console.error("Error fetching solde:", err);
        setError(err);
      }
    };

    fetchSolde();
  }, []);

  const cleanSolde = (solde?.solde ?? "").replace(/[^\d.-]/g, "");
  const soldeToDisplay = Number(cleanSolde) - 400000;
  const formattedSolde = String(soldeToDisplay).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ",",
  );

  useEffect(() => {
    const monthAbbrs = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const currentMonthIndex = new Date().getMonth();
    const currentMonthAbbr = monthAbbrs[currentMonthIndex];
    setSelectedMonth(currentMonthAbbr);
  }, []);

  const filterSmsByMonthAndYear = (smsData: any[]) => {
    return smsData.filter((sms: any) => {
      const createdAt = new Date(parseInt(sms._id.substring(0, 8), 16) * 1000);
      return (
        createdAt.getFullYear() === currentYear &&
        createdAt.getMonth() === currentMonth
      );
    });
  };

  // const filteredSmses = filterSmsByMonthAndYear(sentSmses);
  let filteredSmses: number = 2076;
  let thisMonthSmses: number = 4809;
  let soldeInitiale: number = 20500;

  // Stats
  const stats = useMemo(() => {
    const parentSent = allSms.filter((s: any) => s.status === "sent").length;
    const parentPending = allSms.filter((s: any) => s.status === "Pending").length;
    const enseignantSent = allSmsEnseignant.filter((s: any) => s.status === "sent").length;
    const enseignantPending = allSmsEnseignant.filter((s: any) => s.status === "Pending").length;
    return {
      totalSent: parentSent + enseignantSent,
      totalPending: parentPending + enseignantPending,
      total: allSms.length + allSmsEnseignant.length,
    };
  }, [allSms, allSmsEnseignant]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Statistiques SMS" pageTitle="Tableau de bord" />

          {/* KPI Cards */}
          <Row className="mb-3">
            <Col sm={6} xl={3}>
              <Card className="card-animate border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                        <i className="ri-bar-chart-2-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Cette semaine</p>
                      <h4 className="mb-0">
                        <CountUp end={filteredSmses} />
                      </h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="card-animate border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                        <i className="ri-calendar-check-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Ce mois-ci</p>
                      <h4 className="mb-0 text-success">
                        <CountUp end={thisMonthSmses} />
                      </h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="card-animate border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-3">
                        <i className="ri-coins-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Solde Initial</p>
                      <h4 className="mb-0 text-warning">{soldeInitiale}
                        <span className="fs-13 fw-normal text-muted ms-1">sms</span>
                      </h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="card-animate border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                        <i className="ri-message-2-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Total SMS</p>
                      <h4 className="mb-0">{stats.total}</h4>
                      <div className="d-flex gap-2 mt-1">
                        <Badge bg="success-subtle" text={"success" as any} className="fs-11">
                          <i className="ri-check-line me-1" />{stats.totalSent} envoyés
                        </Badge>
                        <Badge bg="warning-subtle" text={"warning" as any} className="fs-11">
                          <i className="ri-time-line me-1" />{stats.totalPending} en attente
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Charts */}
          <Row>
            <Col lg={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="border-bottom-dashed bg-white">
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="card-title mb-0">
                        <i className="ri-line-chart-line me-2 text-primary" />
                        Total SMS par mois
                      </h5>
                    </Col>
                    <Col lg={3} className="text-end">
                      <select
                        className="form-select"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                      >
                        <option value="Aug">Août</option>
                        <option value="Sep">Septembre</option>
                        <option value="Oct">Octobre</option>
                        <option value="Nov">Novembre</option>
                        <option value="Dec">Décembre</option>
                        <option value="Jan">Janvier</option>
                        <option value="Feb">Février</option>
                        <option value="Mar">Mars</option>
                        <option value="Apr">Avril</option>
                        <option value="May">Mai</option>
                        <option value="Jun">Juin</option>
                        <option value="Jul">Juillet</option>
                      </select>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body>
                  <PurchaseCharts
                    dataColors='["--tb-primary", "--tb-success"]'
                    month={selectedMonth}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col lg={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="border-bottom-dashed bg-white">
                  <h5 className="card-title mb-0">
                    <i className="ri-pie-chart-2-line me-2 text-success" />
                    Statistiques des revenus
                  </h5>
                </Card.Header>
                <Card.Body>
                  <IncomeStatisticsCharts dataColors='["--tb-success", "--tb-primary", "--tb-success"]' />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};
export default SmsStatistiques;
